import * as React from 'react';
import { useIntl } from 'react-intl';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';

import { Checks } from 'src/common/icons';
import { passesRequires } from 'src/chat/nudges/starterGates';
import { useStarterGates } from 'src/chat/nudges/useStarterGates';
import AgentLogo from './AgentLogo';
import {
  EXTERNAL_AGENT_ORDER,
  EXTERNAL_AGENTS,
  EXTERNAL_PROMPTS,
  ExternalPromptId,
  resolveExternalAgent,
} from './externalAgents';
import { useExternalAgent } from './useExternalAgent';
import messages from './messages';
import styles from './ExternalAgentMenu.module.scss';

/**
 * The AI Agent panel header's way out to an external agent (Claude by
 * default) that has the FileSpin MCP connector. It lives in the panel, not
 * the app header, so the header's AI affordances are all the in-dashboard
 * agent's; someone who has opened the panel is the one thinking about AI.
 *
 * One icon button that opens the menu. The first item depends on the user's
 * connected apps: with the connector, open the agent with a get-started
 * prompt; without it, connect (the setup guide), flagged by a "+" badge on
 * the logo. Then more prompts, the default-tool picker and the setup guide.
 */
export const ExternalAgentMenu: React.FC = () => {
  const { formatMessage } = useIntl();
  const { agent, setAgentId, status, openPrompt, openSetup, openPrimary } = useExternalAgent();
  const { hydrated, ctx } = useStarterGates();
  const needsSetup = status === 'not_connected';
  const values = { agent: agent.name };

  // Gated prompts stay hidden until addons/schemas are known, rather than
  // flashing in on every page load.
  const prompts = EXTERNAL_PROMPTS.filter(
    (p) => !p.requires || (hydrated && passesRequires(p.requires, ctx)),
  );

  const items: MenuProps['items'] = [
    {
      key: 'primary',
      label: formatMessage(needsSetup ? messages.connectAgent : messages.openInTitle, values),
      icon: <AgentLogo agent={agent.id} className={styles.menuLogo} />,
      className: styles.primaryItem,
    },
    { type: 'divider' },
    {
      type: 'group',
      key: 'prompts',
      label: formatMessage(messages.promptsHeading),
      children: prompts.map((p) => ({
        key: `prompt:${p.id}`,
        label: formatMessage(messages[`prompt_${p.id}_label` as keyof typeof messages]),
      })),
    },
    { type: 'divider' },
    {
      key: 'agent',
      label: formatMessage(messages.defaultTool),
      popupClassName: styles.menu,
      children: EXTERNAL_AGENT_ORDER.map((id) => ({
        key: `agent:${id}`,
        icon: <AgentLogo agent={id} className={styles.menuLogo} />,
        label: (
          <span className={styles.agentOption}>
            {EXTERNAL_AGENTS[id].name}
            {id === agent.id && <Checks className={styles.check} aria-hidden />}
          </span>
        ),
      })),
    },
    // Already the first item when not connected.
    ...(needsSetup
      ? []
      : [{ type: 'divider' as const }, { key: 'mcp', label: formatMessage(messages.configureMcp) }]),
  ];

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    const [kind, value] = key.split(':');
    if (kind === 'primary') openPrimary();
    else if (kind === 'prompt') openPrompt(value as ExternalPromptId);
    else if (kind === 'agent') setAgentId(resolveExternalAgent(value).id);
    else if (kind === 'mcp') openSetup();
  };

  const label = formatMessage(messages.useInAgent, values);

  return (
    <Dropdown
      menu={{ items, onClick: onMenuClick, selectable: false }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName={styles.menu}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-label={label}
        title={label}
        data-test="external-agent-menu"
        data-status={status}
      >
        <AgentLogo agent={agent.id} className={styles.logo} />
        {needsSetup && <span className={styles.setupBadge} aria-hidden>+</span>}
      </button>
    </Dropdown>
  );
};

export default ExternalAgentMenu;
